/**
 * @file `Hypervisor` — namespacer for `admin.hypervisor.*`
 *       corresponding to the `/hypervisors` panel (umbrella #883,
 *       part #891).
 * @module @labcraft/admin/src/client/hypervisor
 *
 * One `CoreLifecycle`-based namespacer per UI panel. Owns no transport
 * of its own — the parent `Admin` hands a reference to the shared
 * `HttpTransport` at construction; logs/metrics bubble under
 * `admin.hypervisor.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport, WsTransport } from './index.ts'
import {
  ForceExpirePinMachineRoute,
  ListHypervisorMachinesRoute,
  OperatorSuspendMachineRoute,
  type HypervisorListResponse,
} from '../shared/hypervisor_routes.ts'
import { AdminTerminal } from './terminal.ts'
import { AdminFleetLifecycle } from './fleet.ts'

/** Read namespacer for the Hypervisor panel. */
export class Hypervisor extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport
  private readonly ws: WsTransport

  constructor(http: HttpTransport, ws: WsTransport) {
    super({ id: 'hypervisor' })
    this.http = http
    this.ws = ws
  }

  /**
   * List hypervisor machines. `connected` is `false` until an
   * admin→hypervisor connection is wired; `machines` is empty then.
   *
   * @returns The machine list + connection state.
   */
  async list(): Promise<HypervisorListResponse> {
    return this.http.request(ListHypervisorMachinesRoute)
  }

  /**
   * `POST /api/hypervisor/:id/operator-suspend` — suspend a running lab
   * from the control plane (umbrella #1258 Part H / #1337). Drives the
   * owner's reclaim path and stamps the `operator-suspend` reason so the
   * learner overlay reads "an operator paused your lab." There is
   * deliberately no `resume` counterpart — the admin command set is a
   * strict subset of owner operations (core invariant 1); a suspended
   * lab resumes only when its owner dials back in. Idempotent on
   * already-suspended / unknown ids.
   *
   * @param id - The hypervisor Machine id to suspend.
   */
  async operatorSuspend(id: string): Promise<void> {
    await this.http.request(OperatorSuspendMachineRoute, { params: { id } })
  }

  /**
   * `POST /api/hypervisor/:id/force-expire-pin` — force-expire a VM's
   * engagement pin from the control plane (umbrella #1258 Part H / #1340).
   * Flags the pin unavailable owner-side so the learner's next resume
   * clean-refreshes onto CURRENT content (`content-refreshed`, #1333)
   * instead of restoring a snapshot on stale/pruned/security-expired
   * layers. A bounded command — it only flags a pin, never resumes / boots
   * (the owner is the gate). Idempotent on already-flagged / pin-less /
   * unknown ids.
   *
   * @param id - The hypervisor Machine id whose pin to force-expire.
   */
  async forceExpirePin(id: string): Promise<void> {
    await this.http.request(ForceExpirePinMachineRoute, { params: { id } })
  }

  /**
   * Open a read-only observer over the fleet-lifecycle stream (umbrella
   * #1258 Part H / #1340). The returned {@link AdminFleetLifecycle} yields
   * one frame (id + new state + reason) per Machine lifecycle transition
   * across the whole fleet; the panel's freshness hook invalidates its
   * machine-list cache on each frame so rows + action affordances track
   * reality without reconcile-polling. Call `attach()` on it to open the
   * WS; `close()` tears it down on unmount.
   *
   * @returns A fresh, not-yet-attached fleet-lifecycle observer driver.
   */
  subscribeFleetLifecycle(): AdminFleetLifecycle {
    return new AdminFleetLifecycle(this.ws)
  }

  /**
   * Open a read-only terminal observer over the machine `id`. The
   * returned {@link AdminTerminal} is receive-only — pty stdout bytes
   * plus the mirrored learner winsize — and is torn down via its
   * `close()` on unmount. Call `attach()` on it to open the WS.
   *
   * @param id - The hypervisor Machine id to observe.
   * @returns A fresh, not-yet-attached observer driver.
   */
  terminal(id: string): AdminTerminal {
    return new AdminTerminal(this.ws, id)
  }
}
